import { useTranslation } from 'react-i18next'
import {
    Box,
    Divider,
    IconButton,
    ListItem,
    ListItemText,
    Stack,
    Typography,
    Chip,
    Tooltip,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { MedicalActRecord } from '@/core/models/medical-act.model'
import { formatPrice, formatDate } from '../model/useMedicalActRecords'

export type MedicalActRecordItemProps = {
    record: MedicalActRecord
    index: number
    readOnly?: boolean
    isDeleting?: boolean
    onDelete: (recordId: string) => void
}

export function MedicalActRecordItem({
    record,
    index,
    readOnly = false,
    isDeleting = false,
    onDelete,
}: MedicalActRecordItemProps) {
    const { t } = useTranslation()
    const templateName = typeof record.template === 'object' ? record.template.name : 'Unknown Act'
    const templateCode = typeof record.template === 'object' ? record.template.code : undefined
    const performedByName = typeof record.performedBy === 'object'
        ? `${record.performedBy.userInfo?.name} ${record.performedBy.userInfo?.surname}`
        : undefined

    return (
        <Box>
            {index > 0 && <Divider />}
            <ListItem
                disablePadding
                sx={{ py: 1 }}
                secondaryAction={
                    !readOnly && (
                        <Tooltip title={t('common.delete', { defaultValue: 'Delete' })}>
                            <IconButton
                                edge="end"
                                size="small"
                                color="error"
                                onClick={() => onDelete(record._id)}
                                disabled={isDeleting}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )
                }
            >
                <ListItemText
                    primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2">
                                {templateName}
                            </Typography>
                            {templateCode && (
                                <Chip
                                    label={templateCode}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                    }
                    secondary={
                        <Stack direction="row" spacing={2} mt={0.5}>
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(record.performedAt)}
                            </Typography>
                            <Chip
                                label={formatPrice(record.priceAtTime)}
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                            {performedByName && (
                                <Typography variant="caption" color="text.secondary">
                                    {t('medicalAct.section.by', { defaultValue: 'by' })} {performedByName}
                                </Typography>
                            )}
                        </Stack>
                    }
                />
            </ListItem>
        </Box>
    )
}
